import React from 'react';
import { motion } from 'motion/react';
import { PublicHeader } from '../../components/shell/PublicHeader';
import { PublicFooter } from '../../components/shell/PublicFooter';
import { SEO } from '../../components/SEO';
import { cn } from '../../lib/utils';
import { 
  Calculator, 
  FileText, 
  CheckSquare, 
  ArrowRight,
  TrendingUp,
  Boxes,
  HelpCircle,
  Gavel,
  Clock,
  DollarSign,
  Users,
  Target,
  BarChart,
  ShieldAlert,
  Briefcase,
  Scale,
  Image as ImageIcon,
  Sparkles,
  Gamepad2,
  Search,
  X,
  FileCheck
} from 'lucide-react';
import { Badge, Button } from '../../components/ui';
import { useNavigate, Link } from 'react-router-dom';
import { getToolSegment } from '../../lib/toolCopy';

export interface ToolItem {
  slug: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  category: string;
  badge?: string;
}

export const TOOLS_LIST: ToolItem[] = [
  {
    slug: 'dues-split-calculator',
    title: 'Dues Split Calculator',
    description: 'Calculate instant transaction fee distributions and revenue splits between national headquarters, regions, and local chapters.',
    icon: <Calculator className="w-6 h-6 text-emerald-600" />,
    category: 'Finance',
    badge: 'Featured'
  },
  {
    slug: 'bylaws-templates-builder',
    title: 'Bylaws & Constitution Builder',
    description: 'Generate starter markdown-ready organization constitutions, local bylaws, and code of conduct boilerplates.',
    icon: <FileText className="w-6 h-6 text-purple-600" />,
    category: 'Governance',
    badge: 'Featured'
  },
  {
    slug: 'hoa-budget-tracker',
    title: 'HOA Budgeting & Reserve Tracking Suite',
    description: 'Model operating assessments, collection delinquency, and 10-year reserve component schedules with printable board reports.',
    icon: <BarChart className="w-6 h-6 text-green-600" />,
    category: 'Finance',
    badge: 'Featured'
  },
  {
    slug: 'meeting-agenda-template',
    title: 'Board Meeting Agenda Builder & Agenda Maker',
    description: 'Build board agendas online, run meetings with a live countdown timer, take minutes, and track motions with PDF exports.',
    icon: <CheckSquare className="w-6 h-6 text-indigo-600" />,
    category: 'Governance',
    badge: 'Featured'
  },
  {
    slug: 'quorum-and-roll-checker',
    title: 'Quorum & Roll Checker',
    description: 'Quickly compute quorum thresholds, required voters, and motion requirements based on Robert\'s Rules of Order.',
    icon: <CheckSquare className="w-6 h-6 text-blue-600" />,
    category: 'Governance',
    badge: 'Featured'
  },
  {
    slug: 'hq-pitch-generator',
    title: 'HQ Pitch Generator',
    description: 'Generate a customized, persuasive proposal to pitch OrgSets to your National or Regional Headquarters.',
    icon: <Sparkles className="w-6 h-6 text-brand-primary" />,
    category: 'Governance',
    badge: 'Featured'
  },
  {
    slug: 'member-retention-estimator',
    title: 'Member Attrition Estimator',
    description: 'Calculate retention health metrics and model multi-tier recruitment targets to hit your expansion roadmap.',
    icon: <TrendingUp className="w-6 h-6 text-amber-600" />,
    category: 'Analytics'
  },
  {
    slug: 'roberts-rules-motion-finder',
    title: 'Robert\'s Rules Motion Finder',
    description: 'Quickly identify the correct parliamentary motion to use in your meetings based on your objective.',
    icon: <Gavel className="w-6 h-6 text-slate-600" />,
    category: 'Governance'
  },
  {
    slug: 'volunteer-value-calculator',
    title: 'Volunteer Value Calculator',
    description: 'Quantify the financial impact of your volunteers based on estimated hourly equivalent rates.',
    icon: <Clock className="w-6 h-6 text-teal-600" />,
    category: 'Analytics'
  },
  {
    slug: 'event-budget-estimator',
    title: 'Event Budget Estimator',
    description: 'Calculate break-even ticket prices and per-attendee costs for your upcoming community events.',
    icon: <DollarSign className="w-6 h-6 text-green-600" />,
    category: 'Finance'
  },
  {
    slug: 'meeting-minutes-generator',
    title: 'Meeting Minutes Generator',
    description: 'Create standardized markdown-ready meeting minutes to keep your records compliant and organized.',
    icon: <Users className="w-6 h-6 text-indigo-600" />,
    category: 'Governance'
  },
  {
    slug: 'fundraising-tier-calculator',
    title: 'Fundraising Goal Assessor',
    description: 'Break down your large fundraising targets into actionable donor tiers and targeted ask amounts.',
    icon: <Target className="w-6 h-6 text-rose-600" />,
    category: 'Finance'
  },
  {
    slug: 'member-acquisition-cost',
    title: 'Member Acquisition Cost (MAC)',
    description: 'Determine how much it costs your organization to acquire a new member through marketing and outreach.',
    icon: <BarChart className="w-6 h-6 text-orange-600" />,
    category: 'Analytics'
  },
  {
    slug: 'committee-charter-builder',
    title: 'Committee Charter Builder',
    description: 'Draft clear mandates, scopes, and responsibilities for newly formed committees and working groups.',
    icon: <Briefcase className="w-6 h-6 text-cyan-600" />,
    category: 'Governance'
  },
  {
    slug: 'conflict-of-interest-policy',
    title: 'Conflict of Interest Generator',
    description: 'Create a foundational Conflict of Interest policy document for your board and key stakeholders.',
    icon: <ShieldAlert className="w-6 h-6 text-red-600" />,
    category: 'Governance'
  },
  {
    slug: 'sponsorship-package-pricer',
    title: 'Sponsorship Package Pricer',
    description: 'Estimate fair market value for your event sponsorship tiers based on reach and brand exposure.',
    icon: <Boxes className="w-6 h-6 text-blue-500" />,
    category: 'Finance'
  },
  {
    slug: 'voting-weight-calculator',
    title: 'Voting Weight Calculator',
    description: 'Allocate proportional voting strength to delegates based on chapter size or regional membership.',
    icon: <Scale className="w-6 h-6 text-violet-600" />,
    category: 'Governance'
  },
  {
    slug: 'sports-fee-estimator',
    title: 'Sports League Fee Estimator',
    description: 'Calculate player registration fees to cover uniforms, referees, and facility rentals.',
    icon: <Calculator className="w-6 h-6 text-orange-500" />,
    category: 'Finance'
  },
  {
    slug: 'waiver-generator',
    title: 'Liability Waiver Generator',
    description: 'Create standard liability waivers for events, sports teams, and volunteer activities.',
    icon: <FileText className="w-6 h-6 text-red-500" />,
    category: 'Governance'
  },
  {
    slug: 'hoa-reserve-modeler',
    title: 'HOA Reserve Fund Modeler',
    description: 'Model 30-year reserve fund requirements for roof replacements, paving, and capital improvements.',
    icon: <TrendingUp className="w-6 h-6 text-blue-500" />,
    category: 'Finance'
  },
  {
    slug: 'chapter-dues-splitter',
    title: 'Chapter Dues Splitter',
    description: 'Quickly calculate how membership fees distribute between national, state, and local chapter levels.',
    icon: <Calculator className="w-6 h-6 text-purple-500" />,
    category: 'Finance'
  },
  {
    slug: 'alumni-mentorship-match',
    title: 'Alumni Mentorship Matcher',
    description: 'Optimize pairings between new graduates and industry-experienced alumni based on career goals.',
    icon: <Users className="w-6 h-6 text-amber-500" />,
    category: 'Analytics'
  },
  {
    slug: 'ticket-breakeven-calculator',
    title: 'Ticket Break-Even Calculator',
    description: 'Determine the exact number of tickets you need to sell to cover fixed venue and entertainment costs.',
    icon: <DollarSign className="w-6 h-6 text-emerald-500" />,
    category: 'Finance'
  },
  {
    slug: 'volunteer-hours-log',
    title: 'Volunteer Hours Estimator',
    description: 'Project total volunteer hours needed for large-scale events and assign rotational shifts.',
    icon: <Clock className="w-6 h-6 text-teal-500" />,
    category: 'Analytics'
  },
  {
    slug: 'petition-signature-projector',
    title: 'Petition Signature Projector',
    description: 'Calculate daily signature collection targets needed to meet ballot qualification deadlines.',
    icon: <Target className="w-6 h-6 text-rose-500" />,
    category: 'Analytics'
  },
  {
    slug: 'phone-bank-scheduler',
    title: 'Phone Bank Scheduler',
    description: 'Calculate how many volunteers you need per shift to contact your entire voter or donor file.',
    icon: <Clock className="w-6 h-6 text-indigo-500" />,
    category: 'Analytics'
  },
  {
    slug: 'ceu-tracker',
    title: 'CEU Requirement Tracker',
    description: 'Monitor continuing education unit benchmarks for certified professionals in your association.',
    icon: <CheckSquare className="w-6 h-6 text-cyan-500" />,
    category: 'Governance'
  },
  {
    slug: 'sponsorship-roi-calculator',
    title: 'Sponsorship ROI Calculator',
    description: 'Prove value to corporate sponsors by estimating impressions, leads, and brand engagement metrics.',
    icon: <BarChart className="w-6 h-6 text-violet-500" />,
    category: 'Analytics'
  },
  {
    slug: 'pta-fundraiser-planner',
    title: 'PTA Fundraiser Planner',
    description: 'Model different fundraiser scenarios (bake sales, fun runs) to hit classroom equipment goals.',
    icon: <Calculator className="w-6 h-6 text-orange-400" />,
    category: 'Finance'
  },
  {
    slug: 'scout-camp-planner',
    title: 'Scout Camp Budget Planner',
    description: 'Calculate per-scout costs for transportation, food rations, and campsite reservations.',
    icon: <DollarSign className="w-6 h-6 text-green-700" />,
    category: 'Finance'
  },
  {
    slug: 'church-budget-estimator',
    title: 'Church Budget Estimator',
    description: 'Allocate weekly tithes horizontally across pastoral staff, facility upkeep, and missions outreach.',
    icon: <Boxes className="w-6 h-6 text-blue-400" />,
    category: 'Finance'
  },
  {
    slug: 'sports-tournament-budget',
    title: 'Sports Tournament Budget',
    description: 'Consolidate field rentals, referee fees, and trophy expenditures into a single break-even entry fee.',
    icon: <Calculator className="w-6 h-6 text-red-400" />,
    category: 'Finance'
  },
  {
    slug: 'member-retention-calculator',
    title: 'Member Retention Calculator',
    description: 'Analyze year-over-year churn rates and project the lifetime value of an engaged community member.',
    icon: <TrendingUp className="w-6 h-6 text-teal-400" />,
    category: 'Analytics'
  },
  {
    slug: 'garden-bed-estimator',
    title: 'Community Garden Plot Estimator',
    description: 'Calculate soil volume, lumber costs, and seed allocations for new urban community garden beds.',
    icon: <Boxes className="w-6 h-6 text-emerald-400" />,
    category: 'Analytics'
  },
  {
    slug: 'gala-catering-calculator',
    title: 'Gala Catering Calculator',
    description: 'Avoid waste by estimating food quantities, drink tickets, and staff needed for formal plated dinners.',
    icon: <FileText className="w-6 h-6 text-rose-400" />,
    category: 'Finance'
  },
  {
    slug: 'hoa-bid-comparer',
    title: 'HOA Bid Comparer',
    description: 'Weigh distinct vendor proposals for community projects using a standardized grading matrix.',
    icon: <Scale className="w-6 h-6 text-slate-500" />,
    category: 'Governance'
  },
  {
    slug: 'round-robin-schedule-generator',
    title: 'Round-Robin Schedule Maker',
    description: 'Generate mathematically perfect weekly matchups for your intramural or youth sports league.',
    icon: <Target className="w-6 h-6 text-emerald-500" />,
    category: 'Operations'
  },
  {
    slug: 'snake-draft-team-balancer',
    title: 'Fair Snake-Draft Balancer',
    description: 'Paste your roster and perfectly distribute them into randomized, balanced teams using a snake draft.',
    icon: <Users className="w-6 h-6 text-orange-500" />,
    category: 'Operations'
  },
  {
    slug: 'hoa-special-assessment-calculator',
    title: 'Special Assessment Calculator',
    description: 'Break down massive community repairs into exact monthly costs per homeowner for transparent reporting.',
    icon: <Calculator className="w-6 h-6 text-teal-500" />,
    category: 'Finance'
  },
  {
    slug: 'worship-service-run-sheet',
    title: 'Service Run-Sheet Timer',
    description: 'Build your Sunday timeline block-by-block and never let the band or sermon go over time again.',
    icon: <Target className="w-6 h-6 text-purple-500" />,
    category: 'Operations'
  },
  {
    slug: 'philanthropy-profit-margin-predictor',
    title: 'Philanthropy Margin Predictor',
    description: 'Map out your fixed vs variable costs to find your exact break-even point before selling a single ticket.',
    icon: <Calculator className="w-6 h-6 text-pink-500" />,
    category: 'Finance'
  },
  {
    slug: 'tournament-bracket-generator',
    title: 'Ultimate Bracket Generator',
    description: 'An interactive tournament bracket maker for sports, team building, and voting competitions. Paste teams and watch them advance!',
    icon: <Target className="w-6 h-6 text-indigo-500" />,
    category: 'Engagement'
  },
  {
    slug: 'ice-breaker-slot-machine',
    title: 'Ice Breaker Slot Machine',
    description: 'A 3-reel slot machine that generates hilarious, randomized conversation starters for your meetings.',
    icon: <Target className="w-6 h-6 text-purple-500" />,
    category: 'Engagement'
  },
  {
    slug: 'interactive-scoreboard',
    title: 'Interactive Scoreboard & Timer',
    description: 'Projector-ready scoreboard for trivia nights with integrated countdown timers and customizable team names.',
    icon: <Target className="w-6 h-6 text-orange-500" />,
    category: 'Engagement'
  },
  {
    slug: 'virtual-prize-wheel',
    title: 'Virtual Prize Wheel & Dice Roller',
    description: 'Projector-ready animated prize wheel and giant dice roller for community games.',
    icon: <Target className="w-6 h-6 text-pink-500" />,
    category: 'Engagement'
  },
  {
    slug: 'greek-rush-roi',
    title: 'Greek Chapter Rush ROI Estimator',
    description: 'Evaluate the financial and social return on your fraternity or sorority recruitment season with ROI timelines.',
    icon: <TrendingUp className="w-6 h-6 text-fuchsia-600" />,
    category: 'Analytics'
  },
  {
    slug: 'composite-photo-calculator',
    title: 'Composite Photo Cost Calculator',
    description: 'Compute the per-member cost share and total chapter invoice for your annual composite photos.',
    icon: <ImageIcon className="w-6 h-6 text-purple-500" />,
    category: 'Finance'
  },
  {
    slug: 'chapter-house-rent-modeler',
    title: 'Chapter House Rent Modeler',
    description: 'Model room pricing schedules and operating costs for your fraternity or sorority chapter house.',
    icon: <Calculator className="w-6 h-6 text-emerald-500" />,
    category: 'Finance'
  },
  {
    slug: 'panhellenic-quota-calculator',
    title: 'Panhellenic Quota Calculator',
    description: 'Implement the official NPC quota calculation algorithm to determine bid targets per chapter.',
    icon: <CheckSquare className="w-6 h-6 text-blue-600" />,
    category: 'Governance'
  },
  {
    slug: 'alumni-density-mapper',
    title: 'Alumni Chapter Geographic Density Mapper',
    description: 'Visualize alumni zip code clusters to determine optimal locations for new regional chapters.',
    icon: <BarChart className="w-6 h-6 text-orange-500" />,
    category: 'Analytics'
  },
  {
    slug: 'alumni-annual-fund-modeler',
    title: 'Alumni Annual Fund Goal Modeler',
    description: 'Model fundraising goals, participation rates, and week-by-week giving projections for your campaign.',
    icon: <Target className="w-6 h-6 text-rose-500" />,
    category: 'Finance'
  },
  {
    slug: 'alumni-mentorship-capacity',
    title: 'Alumni Mentorship Capacity Planner',
    description: 'Run matching simulations pairing new graduates with alumni mentors based on industry and capacity.',
    icon: <Users className="w-6 h-6 text-teal-600" />,
    category: 'Analytics'
  },
  {
    slug: 'class-reunion-estimator',
    title: 'Class Reunion Budget & RSVP Estimator',
    description: 'Calculate break-even ticket prices and build tiered pricing structures for your class reunion.',
    icon: <DollarSign className="w-6 h-6 text-green-600" />,
    category: 'Finance'
  },
  {
    slug: 'guild-ratio-checker',
    title: 'Guild Journeyman-to-Apprentice Ratio Checker',
    description: 'Verify union or trade guild compliance with mandated journeyman-to-apprentice ratios.',
    icon: <Scale className="w-6 h-6 text-slate-600" />,
    category: 'Governance'
  },
  {
    slug: 'guild-rate-card-builder',
    title: 'Freelancer Guild Rate Card Builder',
    description: 'Build defensible, market-informed hourly and project rate cards based on overhead and target income.',
    icon: <FileText className="w-6 h-6 text-amber-600" />,
    category: 'Finance'
  },
  {
    slug: 'union-strike-fund-calculator',
    title: 'Union Strike Fund Sustainability Calculator',
    description: 'Project week-by-week fund drawdown and calculate required dues to sustain a union strike.',
    icon: <ShieldAlert className="w-6 h-6 text-red-600" />,
    category: 'Finance',
    badge: 'Popular'
  },
  {
    slug: 'union-wage-progression',
    title: 'Union Contract Wage Progression Visualizer',
    description: 'Visualize CBA wage trajectories, COLA adjustments, and real-wage purchasing power over time.',
    icon: <TrendingUp className="w-6 h-6 text-blue-500" />,
    category: 'Analytics'
  },
  {
    slug: 'canvassing-route-optimizer',
    title: 'Canvassing Route Optimizer',
    description: 'Cluster address lists into balanced territories for efficient advocacy door-knocking shifts.',
    icon: <Boxes className="w-6 h-6 text-cyan-500" />,
    category: 'Analytics'
  },
  {
    slug: 'ballot-initiative-forecaster',
    title: 'Ballot Initiative Signature Forecaster',
    description: 'Compute daily collection targets and model the probability of reaching your petition thresholds.',
    icon: <Target className="w-6 h-6 text-rose-600" />,
    category: 'Analytics'
  },
  {
    slug: 'pac-contribution-checker',
    title: 'PAC Contribution Compliance Checker',
    description: 'Verify state and federal contribution limits, prohibited sources, and reporting triggers for your PAC.',
    icon: <Gavel className="w-6 h-6 text-indigo-600" />,
    category: 'Governance'
  },
  {
    slug: 'neighborhood-assessment-calculator',
    title: 'Neighborhood Assessment Calculator',
    description: 'Calculate voluntary neighborhood association fees using flat, proportional, or tiered methods.',
    icon: <Calculator className="w-6 h-6 text-emerald-600" />,
    category: 'Finance'
  },
  {
    slug: 'community-land-trust-calculator',
    title: 'Community Land Trust Calculator',
    description: 'Calculate subsidized purchase prices and affordability metrics for community housing organizations.',
    icon: <DollarSign className="w-6 h-6 text-green-700" />,
    category: 'Finance'
  },
  {
    slug: 'booster-grant-scanner',
    title: 'Booster Club Grant Eligibility Scanner',
    description: 'Cross-reference your school organization profile against standard foundation grant criteria.',
    icon: <HelpCircle className="w-6 h-6 text-violet-500" />,
    category: 'Governance'
  },
  {
    slug: 'pta-budget-vs-actuals',
    title: 'PTA/PTO Budget vs. Actuals Dashboard',
    description: 'Track spending against planned school organization budgets with auto-formatting meeting reports.',
    icon: <BarChart className="w-6 h-6 text-orange-400" />,
    category: 'Finance'
  },
  {
    slug: 'league-standings-tiebreaker',
    title: 'Sports League Standings & Tiebreaker Resolver',
    description: 'Build live league standings and automatically resolve ties using custom rule cascades.',
    icon: <Scale className="w-6 h-6 text-slate-500" />,
    category: 'Analytics'
  },
  {
    slug: 'swim-heat-sheet-generator',
    title: 'Swim Meet Heat Sheet Generator',
    description: 'Automatically seed heats and generate formatted swim meet heat sheets from entry lists.',
    icon: <FileText className="w-6 h-6 text-blue-400" />,
    category: 'Governance'
  },
  {
    slug: 'travel-team-budget-planner',
    title: 'Youth Travel Team Budget Planner',
    description: 'Calculate per-player costs and team fundraising goals across a multi-tournament travel season.',
    icon: <Calculator className="w-6 h-6 text-emerald-400" />,
    category: 'Finance'
  },
  {
    slug: 'church-capital-campaign',
    title: 'Church Capital Campaign Feasibility Modeler',
    description: 'Analyze congregational giving distributions to model the feasibility of your next capital campaign.',
    icon: <TrendingUp className="w-6 h-6 text-teal-500" />,
    category: 'Finance'
  },
  {
    slug: 'church-small-group-rebalancer',
    title: 'Church Small Group Rebalancer',
    description: 'Identify overloaded groups and calculate ideal rebalancing scenarios for your church small groups.',
    icon: <Users className="w-6 h-6 text-amber-500" />,
    category: 'Analytics'
  },
  {
    slug: 'board-skill-gap-analyzer',
    title: 'Nonprofit Board Skill Gap Analyzer',
    description: 'Assess board competencies and identify the most critical recruitment priorities for your non-profit.',
    icon: <BarChart className="w-6 h-6 text-rose-500" />,
    category: 'Governance'
  },
  {
    slug: 'nonprofit-990-ratio-checker',
    title: 'Nonprofit 990 Ratio Health Checker',
    description: 'Benchmark your Form 990 against standard charity watchdog ratios like the Program Expense Ratio.',
    icon: <CheckSquare className="w-6 h-6 text-cyan-600" />,
    category: 'Finance',
    badge: 'Popular'
  },
  {
    slug: 'conference-pnl-simulator',
    title: 'Association Conference P&L Simulator',
    description: 'Model full event revenue and expenses to project breakeven numbers for your annual conference.',
    icon: <Calculator className="w-6 h-6 text-purple-600" />,
    category: 'Finance'
  },
  {
    slug: 'certification-pricing-calculator',
    title: 'Certification Program Pricing Calculator',
    description: 'Price association certification exams and renewals to achieve your target cost-recovery percentage.',
    icon: <DollarSign className="w-6 h-6 text-emerald-600" />,
    category: 'Finance'
  },
  {
    slug: 'endowment-runway-calculator',
    title: 'Alumni Scholarship Endowment Runway Calculator',
    description: 'Simulate perpetual endowment health under varying market returns and scholarship payout rules.',
    icon: <TrendingUp className="w-6 h-6 text-blue-600" />,
    category: 'Finance'
  },
  {
    slug: 'multi-chapter-ballot-validator',
    title: 'Multi-Chapter Election Ballot Validator',
    description: 'Validate weighted delegation ballots and instantly compute outcomes for complex convention elections.',
    icon: <Gavel className="w-6 h-6 text-slate-600" />,
    category: 'Governance'
  },
  {
    slug: 'bingo-caller-board',
    title: 'Bingo Caller & Number Tracker',
    description: 'A digital bingo caller that randomly draws numbers and automatically tracks all previously called balls for the announcer.',
    icon: <Target className="w-6 h-6 text-purple-600" />,
    category: 'Engagement'
  },
  {
    slug: 'bingo-card-generator',
    title: 'Printable Bingo Card Generator',
    description: 'Generate sets of completely randomized, unique Bingo playing cards that you can easily print and cut out for your group.',
    icon: <Boxes className="w-6 h-6 text-pink-600" />,
    category: 'Engagement'
  },
  {
    slug: 'digital-raffle-drum',
    title: 'Digital Raffle Drum & Virtual Ticket Spinner',
    description: 'A virtual spinning raffle drum with sound effects, ticker animation, and confetti for live charity events.',
    icon: <Sparkles className="w-6 h-6 text-amber-500" />,
    category: 'Engagement',
    badge: 'Popular'
  },
  {
    slug: 'reverse-raffle-board',
    title: 'Reverse Raffle Elimination Draw Board',
    description: 'Projector-ready elimination raffle board for banquets and booster clubs. Eliminate tickets with sound effects until the last ticket wins.',
    icon: <Target className="w-6 h-6 text-[#FF0055]" />,
    category: 'Engagement',
    badge: 'New'
  },
  {
    slug: 'raffle-ticket-generator',
    title: 'Printable Numbered Raffle Ticket Generator',
    description: 'Generate consecutive numbered printable raffle tickets with customizable stubs, disclaimers, and event logos.',
    icon: <Boxes className="w-6 h-6 text-blue-500" />,
    category: 'Engagement'
  },
  {
    slug: 'silent-auction-bid-sheet',
    title: 'Silent Auction Bid Sheet Generator',
    description: 'Generate professional, ready-to-print silent auction bid sheets for your next charity gala or fundraiser.',
    icon: <DollarSign className="w-6 h-6 text-emerald-600" />,
    category: 'Finance'
  },
  {
    slug: 'reunion-name-tags',
    title: 'Class Reunion Name Tag Maker',
    description: 'Instantly generate perfectly aligned printable name tags for your class reunion attendees.',
    icon: <Users className="w-6 h-6 text-blue-600" />,
    category: 'Engagement'
  },
  {
    slug: 'ceu-certificate-maker',
    title: 'CEU Attendance Certificate Maker',
    description: 'Generate official, printable certificates of completion for attendees at professional seminars.',
    icon: <FileText className="w-6 h-6 text-purple-600" />,
    category: 'Governance'
  },
  {
    slug: 'sign-up-sheet-generator',
    title: 'Event Sign-Up Sheet Generator',
    description: 'Generate clean, lined sign-up sheets for volunteer shifts, potlucks, or event registration.',
    icon: <FileText className="w-6 h-6 text-cyan-600" />,
    category: 'Operations'
  },
  {
    slug: 'donation-receipt-template',
    title: '501c3 Donation Receipt Generator',
    description: 'Generate IRS-compliant, printable donation receipts for your non-profit tax acknowledgments.',
    icon: <DollarSign className="w-6 h-6 text-green-600" />,
    category: 'Finance'
  },
  {
    slug: 'membership-certificate',
    title: 'Official Membership Certificate Maker',
    description: 'Generate elegant, printable membership certificates & induction awards with customizable seals, borders, and signer blocks.',
    icon: <Target className="w-6 h-6 text-amber-600" />,
    category: 'Engagement',
    badge: 'Popular'
  },
  {
    slug: 'volunteer-hour-verification',
    title: 'Volunteer Hour Verification Letter & Certificate Maker',
    description: 'Generate official signed volunteer service verification letters and certificates for schools, scholarships, and court compliance.',
    icon: <FileCheck className="w-6 h-6 text-teal-600" />,
    category: 'Governance',
    badge: 'New'
  },
  {
    slug: 'event-itinerary-builder',
    title: 'Printable Event Itinerary Builder',
    description: 'Build a clean, printable daily itinerary for your next conference, retreat, or multi-day event.',
    icon: <Clock className="w-6 h-6 text-orange-600" />,
    category: 'Operations'
  },
  {
    slug: 'volunteer-sign-in-sheet',
    title: 'Volunteer Sign-In Sheet Generator',
    description: 'Generate a formatted, printable sign-in sheet to track arrival times and hours for volunteers.',
    icon: <Users className="w-6 h-6 text-teal-600" />,
    category: 'Operations'
  },
  {
    slug: 'hoa-violation-door-hanger',
    title: 'HOA Reminder Door Tag Maker',
    description: 'Generate professional, polite violation notices and reminder door hangers for your HOA.',
    icon: <FileText className="w-6 h-6 text-red-600" />,
    category: 'Governance'
  },
  {
    slug: 'banquet-place-cards',
    title: 'Banquet Table Place Card Maker',
    description: 'Generate printable folding tent cards for assigned seating at your next banquet or gala.',
    icon: <Users className="w-6 h-6 text-pink-600" />,
    category: 'Engagement'
  },
  {
    slug: 'storyboard-grid-printable',
    title: 'Client Sketch Grid Generator',
    description: 'Generate high-resolution printable storyboard grids and wireframe templates for client meetings.',
    icon: <Boxes className="w-6 h-6 text-slate-600" />,
    category: 'Operations'
  },
  {
    slug: 'political-door-hanger',
    title: 'Canvassing Door Hanger Formatter',
    description: 'Generate customized, printable door hangers for your political campaign canvassing efforts.',
    icon: <TrendingUp className="w-6 h-6 text-blue-600" />,
    category: 'Operations'
  },
  {
    slug: 'storefront-notice-sign',
    title: 'Storefront "Notice" Sign Maker',
    description: 'Generate a clean, highly visible PDF sign to display temporary hours or notices on your business.',
    icon: <ShieldAlert className="w-6 h-6 text-amber-500" />,
    category: 'Operations'
  },
  {
    slug: 'emergency-roster-card',
    title: 'Coach\'s Lanyard Emergency Roster',
    description: 'Generate a compact, printable emergency contact roster designed to fit inside a coach lanyard badge.',
    icon: <Users className="w-6 h-6 text-orange-500" />,
    category: 'Operations'
  },
  {
    slug: 'book-club-pamphlet',
    title: 'Discussion Guide Pamphlet Maker',
    description: 'Generate a printable, folding discussion guide with custom prompts for your next book club.',
    icon: <FileText className="w-6 h-6 text-indigo-500" />,
    category: 'Engagement'
  },
  {
    slug: 'wifi-qr-poster',
    title: 'QR Code Poster Generator (Wi-Fi)',
    description: 'Generate a branded, high-resolution printable poster featuring a QR code for instant guest Wi-Fi access.',
    icon: <Target className="w-6 h-6 text-teal-500" />,
    category: 'Operations'
  },
  {
    slug: 'neighborhood-newsletter-template',
    title: '1-Page Neighborhood Newsletter Maker',
    description: 'Generate a clean, single-page printable newsletter to distribute updates in your neighborhood.',
    icon: <FileText className="w-6 h-6 text-purple-500" />,
    category: 'Engagement'
  },
  {
    slug: 'roberts-rules-cheat-sheet',
    title: 'Robert\'s Rules Cheat Sheet Maker',
    description: 'Generate a printable reference guide detailing standard parliamentary motions and voting thresholds.',
    icon: <Gavel className="w-6 h-6 text-slate-600" />,
    category: 'Governance'
  },
  {
    slug: 'image-upscaler',
    title: 'Image Upscaler & Watermark Remover',
    description: 'Paint over any watermark or logo and it is reconstructed from the surrounding pixels, then upscaled. Runs entirely in your browser.',
    icon: <ImageIcon className="w-6 h-6 text-fuchsia-500" />,
    category: 'Utilities'
  },
  {
    slug: 'deep-sea-explorer',
    title: 'Deep Sea Explorer',
    description: 'Play an interactive submarine arcade game and save your high scores to the backend.',
    icon: <Gamepad2 className="w-6 h-6 text-brand-primary" />,
    category: 'Entertainment'
  },
  {
    slug: 'org-market-wars',
    title: 'Org Market Wars',
    description: 'A 90s TI calculator-style commodity trading simulator. Out-trade rival chapters across 30 days.',
    icon: <Briefcase className="w-6 h-6 text-amber-500" />,
    category: 'Entertainment'
  },
  {
    slug: 'orgle',
    title: 'Orgle: The Bureaucracy Word Game',
    description: 'Play our daily 5-letter word game for committee members, board chairs, and organization leaders.',
    icon: <Gamepad2 className="w-6 h-6 text-[#0044FF]" />,
    category: 'Entertainment'
  },
  {
    slug: 'node-man',
    title: 'NodeMan: Compliance Dash',
    description: 'A classic Pac-Man spinoff arcade maze game. Dodge Red Tape and IRS Audits while collecting Dues Dots.',
    icon: <Gamepad2 className="w-6 h-6 text-yellow-500" />,
    category: 'Entertainment'
  },
  {
    slug: 'org-run',
    title: 'OrgRun: Bureaucracy Runner',
    description: 'An endless runner game. Sprint through office hallways, dodge paperwork & meeting invites, collect star coins.',
    icon: <Gamepad2 className="w-6 h-6 text-blue-500" />,
    category: 'Entertainment'
  },
  {
    slug: 'micro-machines',
    title: 'Micro Machines: Office Edition',
    description: 'Top-down 90s retro racer. Race mini cars around office desk tracks, drift corners, and outpace AI coworkers.',
    icon: <Gamepad2 className="w-6 h-6 text-red-500" />,
    category: 'Entertainment'
  }
];

export function ToolsDirectoryPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedCategory, setSelectedCategory] = React.useState('All');
  const [audience, setAudience] = React.useState<'all' | 'org' | 'fun'>('all');

  const CATEGORIES = ['All', 'Finance', 'Governance', 'Analytics', 'Operations', 'Utilities', 'Engagement', 'Entertainment'];
  const featuredAdminCount = TOOLS_LIST.filter(tool => tool.badge === 'Featured').length;

  const filteredTools = TOOLS_LIST.filter(tool => {
    const query = searchQuery.toLowerCase().trim();
    const matchesSearch = !query || 
      tool.title.toLowerCase().includes(query) ||
      tool.description.toLowerCase().includes(query) ||
      tool.category.toLowerCase().includes(query);

    const matchesCategory = selectedCategory === 'All' || tool.category.toLowerCase() === selectedCategory.toLowerCase();
    const segment = getToolSegment(tool.category, tool.slug);
    const matchesAudience =
      audience === 'all' ||
      (audience === 'org' ? segment === 'operator' : segment !== 'operator');

    return matchesSearch && matchesCategory && matchesAudience;
  }).sort((a, b) => Number(Boolean(b.badge)) - Number(Boolean(a.badge)));

  const toolSchema = [
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": TOOLS_LIST.map((tool, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `https://orgsets.com/tools/${tool.slug}`,
        "name": tool.title,
        "description": tool.description
      }))
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://orgsets.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Tools",
          "item": "https://orgsets.com/tools"
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen transition-colors duration-300 font-sans bg-white text-black dark:bg-black dark:text-white">
      <SEO 
        title="SEO & Community Tools | OrgSets" 
        description="Calculate dues, draft bylaws, and test quorum statistics in our administrative toolbox."
        url="https://orgsets.com/tools"
        schema={toolSchema}
      />
      <PublicHeader />

      <main className="pb-24">
        {/* Header */}
        <section className="grid grid-cols-1 lg:grid-cols-2 border-b-[8px] border-black dark:border-white bg-white dark:bg-black text-black dark:text-white relative overflow-hidden mb-16">
          <div className="p-8 pt-28 md:p-16 md:pt-32 lg:p-12 lg:pt-32 xl:p-24 xl:pt-40 flex flex-col justify-center border-b-[8px] lg:border-b-0 lg:border-r-[8px] border-black dark:border-white">
            <div className="mb-6">
              <span className="bg-black text-white dark:bg-white dark:text-black px-4 py-2 font-black uppercase tracking-widest text-sm border-[4px] border-black dark:border-white">Admin Toolbox</span>
            </div>
            <h1 className="text-5xl sm:text-6xl md:text-8xl lg:text-6xl xl:text-7xl 2xl:text-[100px] font-black font-display uppercase tracking-tighter break-words leading-[0.85] mb-8">
              Community Toolset
            </h1>
            <p className="text-xl md:text-2xl font-bold max-w-xl leading-snug">
              Interactive calculators and utility frameworks to streamline governance complexity and eliminate manual spreadsheets.
            </p>
          </div>
          <div className="bg-[#FFD600] flex flex-col justify-center items-center p-8 md:p-16 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#0044FF] rounded-bl-full border-b-[8px] border-l-[8px] border-black dark:border-white" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-pink-500 rounded-tr-full border-t-[8px] border-r-[8px] border-black dark:border-white" />
            <div className="relative z-10 w-full max-w-md border-[8px] border-black dark:border-white shadow-[16px_16px_0px_#000] dark:shadow-[16px_16px_0px_#fff] bg-black aspect-video flex flex-col items-center justify-center text-white space-y-4">
              <Boxes className="w-20 h-20" />
              <span className="font-black uppercase tracking-widest text-xl">{TOOLS_LIST.length} Utilities</span>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-6">

          {/* Hub Navigation */}
          <div className="flex flex-wrap justify-center gap-2 mb-12 relative z-20">
            <Link to="/tools">
              <span className="inline-block border-[2px] border-black dark:border-white bg-black text-white dark:bg-white dark:text-black px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:bg-brand-primary transition-colors cursor-pointer">All Tools</span>
            </Link>
            <Link to="/tools/hoa">
              <span className="inline-block border-[2px] border-black dark:border-white bg-white text-black dark:bg-black dark:text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors cursor-pointer">HOA Tools</span>
            </Link>
            <Link to="/tools/union">
              <span className="inline-block border-[2px] border-black dark:border-white bg-white text-black dark:bg-black dark:text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors cursor-pointer">Union Tools</span>
            </Link>
            <Link to="/tools/youth-sports">
              <span className="inline-block border-[2px] border-black dark:border-white bg-white text-black dark:bg-black dark:text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors cursor-pointer">Youth Sports</span>
            </Link>
            <Link to="/tools/nonprofit">
              <span className="inline-block border-[2px] border-black dark:border-white bg-white text-black dark:bg-black dark:text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors cursor-pointer">Nonprofit</span>
            </Link>
            <Link to="/tools/church">
              <span className="inline-block border-[2px] border-black dark:border-white bg-white text-black dark:bg-black dark:text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors cursor-pointer">Church</span>
            </Link>
            <Link to="/tools/association">
              <span className="inline-block border-[2px] border-black dark:border-white bg-white text-black dark:bg-black dark:text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors cursor-pointer">Association</span>
            </Link>
            <Link to="/tools/greek-life">
              <span className="inline-block border-[2px] border-black dark:border-white bg-white text-black dark:bg-black dark:text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors cursor-pointer">Greek Life</span>
            </Link>
            <Link to="/tools/alumni">
              <span className="inline-block border-[2px] border-black dark:border-white bg-white text-black dark:bg-black dark:text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors cursor-pointer">Alumni</span>
            </Link>
            <Link to="/tools/guild">
              <span className="inline-block border-[2px] border-black dark:border-white bg-white text-black dark:bg-black dark:text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors cursor-pointer">Guilds</span>
            </Link>
            <Link to="/tools/advocacy">
              <span className="inline-block border-[2px] border-black dark:border-white bg-white text-black dark:bg-black dark:text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors cursor-pointer">Advocacy</span>
            </Link>
            <Link to="/tools/senior-living">
              <span className="inline-block border-[2px] border-black dark:border-white bg-white text-black dark:bg-black dark:text-white px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:bg-black hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors cursor-pointer">Senior Living</span>
            </Link>
          </div>

          {/* Interactive Search Bar & Category Filter Section */}
          <div className="mb-12 space-y-6">
            <div className="max-w-2xl mx-auto relative">
              <div className="relative border-[4px] border-black dark:border-white shadow-[6px_6px_0px_#0044FF] bg-white dark:bg-black flex items-center">
                <Search className="w-6 h-6 ml-4 text-black dark:text-white shrink-0" />
                <input
                  type="text"
                  placeholder={`Search ${TOOLS_LIST.length} tools by name, keyword, or function...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-4 font-mono font-bold bg-transparent text-black dark:text-white focus:outline-none placeholder:text-gray-400 text-sm md:text-base"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="p-2 mr-2 hover:bg-gray-200 dark:hover:bg-slate-800 text-black dark:text-white"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            {/* Audience Filter Pills */}
            <div className="flex flex-wrap justify-center items-center gap-2">
              <span className="text-xs font-mono font-black uppercase tracking-widest mr-2 text-slate-500">Audience:</span>
              {[
                { id: 'all' as const, label: 'All tools' },
                { id: 'org' as const, label: 'For organizations' },
                { id: 'fun' as const, label: 'Just for fun' },
              ].map((item) => {
                const isActive = audience === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setAudience(item.id)}
                    className={`border-[2px] border-black dark:border-white font-black uppercase text-[10px] tracking-widest px-3.5 py-1.5 transition-all cursor-pointer ${
                      isActive
                        ? 'bg-[#0044FF] text-white shadow-[3px_3px_0px_#000]'
                        : 'bg-white text-black dark:bg-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>

            {/* Category Filter Pills */}
            <div className="flex flex-wrap justify-center items-center gap-2">
              <span className="text-xs font-mono font-black uppercase tracking-widest mr-2 text-slate-500">Filter:</span>
              {CATEGORIES.map((cat) => {
                const isActive = selectedCategory === cat;
                return (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`border-[2px] border-black dark:border-white font-black uppercase text-[10px] tracking-widest px-3.5 py-1.5 transition-all cursor-pointer ${
                      isActive 
                        ? 'bg-[#FFD600] text-black shadow-[3px_3px_0px_#000]' 
                        : 'bg-white text-black dark:bg-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-800'
                    }`}
                  >
                    {cat}
                  </button>
                );
              })}
            </div>

            {/* Active Search & Filter Count Indicator */}
            <div className="text-center font-mono text-xs font-bold text-slate-500 uppercase tracking-widest">
              Showing {filteredTools.length} of {TOOLS_LIST.length} utilities
            </div>
          </div>

          {/* Empty Search State */}
          {filteredTools.length === 0 && (
            <div className="border-[4px] border-black dark:border-white bg-[#FFD600] p-12 text-center text-black space-y-4 max-w-lg mx-auto shadow-[8px_8px_0px_#000]">
              <Search className="w-12 h-12 mx-auto text-black" />
              <h3 className="text-2xl font-black font-display uppercase tracking-tight">No tools found</h3>
              <p className="font-bold text-sm">
                No administrative tools match "{searchQuery}" in category "{selectedCategory}".
              </p>
              <button
                onClick={() => { setSearchQuery(''); setSelectedCategory('All'); setAudience('all'); }}
                className="bg-black text-white font-black uppercase tracking-wider px-6 py-3 border-[2px] border-black"
              >
                Reset Search Filters
              </button>
            </div>
          )}

          {/* Featured Admin Tools Spotlight Header */}
          {selectedCategory === 'All' && !searchQuery && audience === 'all' && (
            <div className="mb-8 border-b-[4px] border-black dark:border-white pb-4 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2.5 py-0.5 bg-[#FFD600] text-black text-[10px] font-black uppercase tracking-wider border border-black shadow-[2px_2px_0px_#000]">
                    Featured
                  </span>
                  <span className="text-xs font-mono font-bold uppercase tracking-widest text-[#0044FF] dark:text-[#FFD600]">
                    Free Tool Suite
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-black font-display uppercase tracking-tight">
                  Featured Admin Tools
                </h2>
                <p className="text-xs font-bold text-gray-500 max-w-xl mt-1">
                  Top-converting financial, governance, and compliance calculators designed for volunteer board officers and treasurers.
                </p>
              </div>
              <div className="text-xs font-mono font-black text-gray-400 uppercase tracking-widest">
                {featuredAdminCount} Pinned Utilities
              </div>
            </div>
          )}

          {/* Tools Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredTools.map((tool, idx) => (
              <motion.div
                key={tool.slug}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (idx % 12) * 0.05 }}
                className="bg-white dark:bg-black border-[4px] border-black dark:border-white shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff] p-6 hover:translate-x-[-4px] hover:translate-y-[-4px] hover:shadow-[12px_12px_0px_#000] dark:hover:shadow-[12px_12px_0px_#fff] transition-all rounded-none group flex flex-col justify-between relative"
              >
                <Link
                  to={`/tools/${tool.slug}`}
                  className="absolute inset-0 z-10"
                  aria-label={`Open tool: ${tool.title}`}
                />
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="w-12 h-12 border-[3px] border-black dark:border-white bg-white dark:bg-black flex items-center justify-center relative z-20">
                      {tool.icon}
                    </div>
                    <div className="flex gap-2 relative z-20 pointer-events-none">
                      <span className="border-[2px] border-black dark:border-white px-2 py-0.5 text-[9px] font-black uppercase tracking-widest bg-white dark:bg-black text-black dark:text-white">{tool.category}</span>
                      {tool.badge && (
                        <span className="border-[2px] border-black dark:border-white px-2 py-0.5 text-[9px] font-black uppercase tracking-widest bg-[#FFD600] text-black">{tool.badge}</span>
                      )}
                    </div>
                  </div>
                  <h3 className="text-xl font-black font-display uppercase tracking-tighter text-black dark:text-white group-hover:text-[#0044FF] dark:group-hover:text-[#FFD600] transition-colors leading-tight">
                    {tool.title}
                  </h3>
                  <p className="text-sm font-bold text-gray-600 dark:text-gray-400 leading-snug">
                    {tool.description}
                  </p>
                </div>
                <div className="pt-4 mt-6 border-t-[3px] border-black dark:border-white flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-black dark:text-white group-hover:text-[#0044FF] dark:group-hover:text-[#FFD600] transition-colors relative z-20 pointer-events-none">
                  <span className="flex items-center gap-1">
                    <Boxes className="w-3.5 h-3.5" /> OPEN
                  </span>
                  <div className="w-8 h-8 border-[3px] border-black dark:border-white flex items-center justify-center group-hover:bg-[#0044FF] group-hover:border-[#0044FF] dark:group-hover:bg-[#FFD600] dark:group-hover:border-[#FFD600] group-hover:text-white dark:group-hover:text-black transition-all bg-white dark:bg-black text-black dark:text-white">
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Need help footer section */}
          <div className="mt-16 text-center max-w-lg mx-auto space-y-6 relative z-20 p-8 border-[4px] border-black dark:border-white bg-[#F5F5F0] dark:bg-[#0A0A0A] shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff]">
            <HelpCircle className="w-12 h-12 text-black dark:text-white mx-auto mb-2" />
            <h4 className="font-black font-display uppercase text-2xl tracking-tight">Need a custom formula?</h4>
            <p className="text-sm font-bold text-gray-700 dark:text-gray-300 leading-relaxed">
              If your organization administers complex dues schedules, custom regional levies, or non-traditional voting weights, our support team can help bootstrap special workflows.
            </p>
            <div className="pt-2">
              <button onClick={() => navigate('/contact')} className="bg-black text-white dark:bg-white dark:text-black border-[4px] border-black dark:border-white font-black uppercase tracking-widest px-8 py-4 transition-all shadow-[6px_6px_0px_#0044FF] dark:shadow-[6px_6px_0px_#FFD600] hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[8px_8px_0px_#0044FF] dark:hover:shadow-[8px_8px_0px_#FFD600] rounded-none cursor-pointer relative z-20">
                Contact Operations
              </button>
            </div>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
